import { Grid, Typography } from "@mui/material";
import { useAchievements } from "api/achievements";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useNavigate } from "react-router-dom";
import { SINGLE_EVENT_TRIGGERED_ACHIEVEMENTS_ID } from "state/tables/types";
import { TriggeredAchievementListItem } from "types/achievements";
import { ListingActions } from "types/listing";
import { links } from "utilities/links";

interface Props {
  customEventId?: string;
  memberId: string;
}

export const TriggeredAchievements = ({ customEventId, memberId }: Props) => {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing(
    SINGLE_EVENT_TRIGGERED_ACHIEVEMENTS_ID,
  );

  const { useMembersTriggeredAchievements } = useAchievements();
  const { data: achievementsList } = useMembersTriggeredAchievements(
    memberId,
    customEventId
      ? {
          filters: [
            {
              condition: "",
              query: "triggerId",
              value: customEventId,
            },
          ],

          order: listing.order,
          page: listing.page,
          rows: listing.rowsPerPage,
          sortKey: listing.property,
        }
      : undefined,
  );

  const headItems = createHeadItems([
    {
      headerText: message({ id: "achievementName" }),
      property: "achievementName",
      isSortable: false,
    },
    {
      headerText: message({ id: "ruleName" }),
      property: "ruleName",
      isSortable: false,
    },
    {
      headerText: message({ id: "ruleID" }),
      property: "ruleId",
      align: "right",
      isSortable: false,
    },
  ]);

  const createItems = (items: TriggeredAchievementListItem[]): Row[] => {
    return items.map((item) => ({
      cells: [
        {
          content: item.achievementName,
          property: "achievementName",
        },
        { content: item.ruleName, property: "ruleName" },
        {
          content: item.ruleId,
          align: "right",
          property: "ruleId",
          dataHjSuppress: true,
        },
      ],
      id: item.triggerId as string,
      metadata: {
        memberId: memberId,
      },
    }));
  };

  const items = !memberId
    ? []
    : achievementsList?.items && customEventId
      ? createItems(achievementsList.items)
      : [];

  return (
    <Grid item xs={12}>
      <Typography marginBottom={6}>
        {message({ id: "triggeredAchievements" })}
      </Typography>

      <OLTable
        tableName={SINGLE_EVENT_TRIGGERED_ACHIEVEMENTS_ID}
        actions={{
          body: [
            {
              action: (_, item?: Row) => {
                navigate(
                  links.singleMemberAchievements(item?.metadata?.memberId),
                );
              },
              hideCondition: (item: Row) => !item?.metadata?.memberId,
              label: message({ id: "seeMemberDetails" }),
            },
          ],
        }}
        data-test-id="triggered-achievements"
        columns={headItems}
        onSort={() => {}}
        pagination={{
          count: calculateCount(
            achievementsList?.total.filtered,
            achievementsList?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage || 25,
        }}
        rows={items}
        sort={{ order: "asc", property: "", rowsPerPage: 10, page: 1 }}
      />
    </Grid>
  );
};
